/**
 * Drug-safety helpers (Sprint 2, security-audit Findings 13 & 14).
 *
 * Two responsibilities:
 *   1. Drug-class allergy matching — replace the fragile substring
 *      match (`drug_name.includes(allergen)`) with a class-aware
 *      lookup. Penicillin → flag Amoxicillin / Ampicillin / etc.
 *      Sulfa → flag Sulfamethoxazole / Bactrim. Etc.
 *   2. Max-dose parsing — pull a numeric upper bound out of the
 *      protocol's `maxDose` text ("max 2 mg", "max 0.4 mg", etc.)
 *      so the form can hard-block doses above it without a
 *      medical-control override.
 *
 * Intentionally local + minimal. A full RxNorm / NDF-RT integration
 * lives outside this codebase; this table covers the meds in the RI
 * formulary plus the common cross-class hits an EMT would expect.
 * Adding new entries is just appending to the table below.
 */

export type DrugClass =
  | 'penicillin'
  | 'cephalosporin'
  | 'sulfonamide'
  | 'opioid'
  | 'nsaid'
  | 'salicylate'
  | 'benzodiazepine'
  | 'iodine_contrast'
  | 'macrolide'
  | 'aminoglycoside'
  | 'fluoroquinolone'
  | 'beta_blocker'
  | 'ace_inhibitor'
  | 'statin';

/**
 * `aliases` are the patterns a patient might TYPE as their allergy
 * (free text in the form). Lowercase, no punctuation. Substrings are
 * matched permissively (`penicillin` matches "Penicillin V", "PCN",
 * "Pen V K", etc.).
 */
interface ClassDef {
  cls: DrugClass;
  aliases: string[];
  members: string[]; // drug names (lowercase) that belong to the class
}

const CLASSES: ClassDef[] = [
  {
    cls: 'penicillin',
    aliases: ['penicillin', 'pcn', 'pen v', 'pen vk', 'amoxicillin', 'amoxil', 'augmentin'],
    members: [
      'penicillin', 'amoxicillin', 'ampicillin', 'augmentin',
      'amoxicillin-clavulanate', 'piperacillin', 'oxacillin',
      'dicloxacillin', 'nafcillin', 'ticarcillin',
    ],
  },
  {
    // ~10% cross-reactivity with penicillin; we surface it as a
    // contraindication (separate class) and let the EMT override.
    cls: 'cephalosporin',
    aliases: ['cephalexin', 'keflex', 'cefazolin', 'ancef', 'ceftriaxone', 'rocephin'],
    members: [
      'cephalexin', 'cefazolin', 'cefuroxime', 'ceftriaxone',
      'cefepime', 'cefdinir', 'cefpodoxime',
    ],
  },
  {
    cls: 'sulfonamide',
    aliases: ['sulfa', 'sulfonamide', 'bactrim', 'septra', 'sulfamethoxazole', 'tmp-smx', 'sulfasalazine'],
    members: [
      'sulfamethoxazole', 'trimethoprim-sulfamethoxazole', 'bactrim',
      'septra', 'sulfasalazine', 'sulfadiazine', 'sulfisoxazole',
    ],
  },
  {
    cls: 'opioid',
    aliases: ['opioid', 'opiate', 'morphine', 'codeine', 'hydrocodone', 'oxycodone', 'fentanyl', 'percocet', 'vicodin', 'dilaudid', 'hydromorphone', 'tramadol', 'methadone', 'demerol'],
    members: [
      'morphine', 'fentanyl', 'hydromorphone', 'oxycodone',
      'hydrocodone', 'codeine', 'methadone', 'tramadol', 'meperidine',
    ],
  },
  {
    cls: 'nsaid',
    aliases: ['nsaid', 'ibuprofen', 'motrin', 'advil', 'naproxen', 'aleve', 'ketorolac', 'toradol', 'meloxicam', 'celecoxib', 'diclofenac', 'indomethacin'],
    members: [
      'ibuprofen', 'naproxen', 'ketorolac', 'meloxicam', 'celecoxib',
      'diclofenac', 'indomethacin', 'piroxicam',
    ],
  },
  {
    cls: 'salicylate',
    aliases: ['aspirin', 'asa', 'salicylate', 'salicylic'],
    members: ['aspirin', 'bismuth subsalicylate'],
  },
  {
    cls: 'benzodiazepine',
    aliases: ['benzo', 'benzodiazepine', 'midazolam', 'versed', 'diazepam', 'valium', 'lorazepam', 'ativan', 'alprazolam', 'xanax', 'clonazepam', 'klonopin'],
    members: [
      'midazolam', 'diazepam', 'lorazepam', 'alprazolam', 'clonazepam',
    ],
  },
  {
    cls: 'iodine_contrast',
    aliases: ['iodine', 'shellfish', 'contrast', 'ct contrast', 'iv contrast'],
    members: ['iodine', 'iohexol', 'iopamidol'],
  },
];

/** Normalize an allergen string for matching. */
function norm(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9 ]/g, ' ').replace(/\s+/g, ' ').trim();
}

/** Returns the classes a typed allergen belongs to. */
export function classesForAllergen(allergen: string): DrugClass[] {
  const a = norm(allergen);
  if (!a || a === 'nka' || a === 'nkda') return [];
  const hits: DrugClass[] = [];
  for (const def of CLASSES) {
    for (const alias of def.aliases) {
      if (a.includes(alias) || alias.includes(a)) {
        hits.push(def.cls);
        break;
      }
    }
  }
  return hits;
}

/** Returns the classes a drug name (e.g., "Amoxicillin", "Fentanyl") belongs to. */
export function classesForDrug(drugName: string): DrugClass[] {
  const d = norm(drugName);
  const hits: DrugClass[] = [];
  for (const def of CLASSES) {
    if (def.members.some((m) => d.includes(m))) hits.push(def.cls);
  }
  return hits;
}

/**
 * Returns a list of human-readable contraindication strings for a
 * drug/allergy pair, e.g. ["Penicillin allergy → Amoxicillin (penicillin class)"].
 * Empty array if the drug is safe to administer per known classes.
 *
 * Falls back to a substring match (the legacy behavior) so a typed
 * allergy outside the class table still produces a warning. Better
 * to over-warn than to silently miss.
 */
export function allergyContraindications(
  drugName: string,
  allergies: Array<{ agent: string }>,
): string[] {
  if (!drugName || allergies.length === 0) return [];
  const drugClasses = classesForDrug(drugName);
  const out: string[] = [];
  const seen = new Set<string>();

  for (const a of allergies) {
    if (!a.agent || a.agent === 'NKA' || a.agent === 'NKDA') continue;
    const allergenClasses = classesForAllergen(a.agent);

    // Class match (the new path — Finding 14).
    for (const cls of allergenClasses) {
      if (drugClasses.includes(cls)) {
        const msg = `Patient allergic to ${a.agent} (${cls} class) — ${drugName} is in the same class`;
        if (!seen.has(msg)) { out.push(msg); seen.add(msg); }
      }
    }
    // Cephalosporin ⇄ penicillin cross-reactivity (~10%).
    if (allergenClasses.includes('penicillin') && drugClasses.includes('cephalosporin')) {
      const msg = `Patient allergic to ${a.agent} (penicillin) — ${drugName} (cephalosporin) has ~10% cross-reactivity`;
      if (!seen.has(msg)) { out.push(msg); seen.add(msg); }
    }
    if (allergenClasses.includes('cephalosporin') && drugClasses.includes('penicillin')) {
      const msg = `Patient allergic to ${a.agent} (cephalosporin) — ${drugName} (penicillin) has ~10% cross-reactivity`;
      if (!seen.has(msg)) { out.push(msg); seen.add(msg); }
    }

    // Legacy substring fallback — catches typed allergies the class
    // table doesn't know about (e.g., "Peanut", "Latex", brand names
    // not in the alias list).
    if (allergenClasses.length === 0) {
      if (norm(drugName).includes(norm(a.agent))) {
        const msg = `Patient allergic to ${a.agent}`;
        if (!seen.has(msg)) { out.push(msg); seen.add(msg); }
      }
    }
  }
  return out;
}

/**
 * Parse the protocol's `maxDose` text into a numeric upper bound and
 * unit. Returns null if it can't be parsed (no hard-block in that
 * case — protocol text is free-form so we fail open).
 *
 * Handles: "2 mg", "max 0.4 mg", "0.5 mg/kg up to 50 mg", "10 mg total".
 */
export function parseMaxDose(maxDoseText: string | null | undefined): { value: number; unit: string } | null {
  if (!maxDoseText) return null;
  // Prefer "up to X UNIT" / "total X UNIT" / "max X UNIT" if present;
  // they're the upper bound when the protocol mixes per-kg + cap.
  const upper = maxDoseText.match(/(?:up to|total|max(?:imum)?)\s*([\d.]+)\s*(mg|mcg|g|ml|units|mEq)/i);
  if (upper) return { value: parseFloat(upper[1]), unit: upper[2].toLowerCase() };
  // Otherwise take the first number+unit pair.
  const first = maxDoseText.match(/([\d.]+)\s*(mg|mcg|g|ml|units|mEq)/i);
  if (first) return { value: parseFloat(first[1]), unit: first[2].toLowerCase() };
  return null;
}

/**
 * Compare a user-entered dose against the protocol max. Returns:
 *   { exceeds: true, max, given } when the input is above the parsed max
 *   { exceeds: false } otherwise (including when units don't match —
 *     we don't try to convert mcg ↔ mg; the dose-entry form should
 *     already constrain the unit to whatever the protocol uses).
 */
export function exceedsMaxDose(
  doseStr: string,
  unit: string,
  maxDoseText: string | null | undefined,
): { exceeds: true; max: number; given: number; unit: string } | { exceeds: false } {
  const max = parseMaxDose(maxDoseText);
  if (!max) return { exceeds: false };
  const given = parseFloat(doseStr);
  if (!Number.isFinite(given)) return { exceeds: false };
  if (max.unit !== unit.toLowerCase()) return { exceeds: false };
  if (given > max.value) {
    return { exceeds: true, max: max.value, given, unit: max.unit };
  }
  return { exceeds: false };
}
